import { useEffect, useState } from "react";
import { IDashboardStats } from "../../models/DashboardStats";
import PriceFormat from "../../utils/priceFormat";
import DashboardTile from "./DashboardTile";
import DashboardStatTableTabs from "../shared/Tabs/DashboardStatTableTabs";

interface IVehicleReferralsTableProps {
  todayStats: IDashboardStats;
  weekStats: IDashboardStats;
  monthStats: IDashboardStats;
}

const tabs = [
  { name: "Today", value: "today" },
  { name: "Week", value: "week" },
  { name: "Month", value: "month" },
];

const ROW_CONFIG: { key: string; label: string; format?: "price" }[] = [
  { key: "vehiclesAwaitingScheduling", label: "Vehicles Awaiting Scheduling" },
  { key: "vehiclesAwaitingCollection", label: "Vehicles Awaiting Collection" },
  { key: "vehiclesCompleted", label: "Vehicles Completed" },
  { key: "vehiclesCancelled", label: "Vehicles Cancelled" },
  { key: "averageVehiclePrice", label: "Average Vehicle Price", format: "price" },
  { key: "averageVehicleValue", label: "Average Vehicle Value", format: "price" },
  { key: "averageCommission", label: "Average Commission", format: "price" },
  { key: "totalCommissionComplete", label: "Total Commission", format: "price" },
];

const COLUMN_CONFIG: { key: string; label: string }[] = [
  { key: "manual", label: "Manual" },
  { key: "widget", label: "Widget" },
  { key: "scrapAVehicle", label: "Scrap a Vehicle" },
  { key: "sellAVehicle", label: "Sell A Vehicle" },
  { key: "carTakeBack", label: "Car Take Back" },
  { key: "motorwise", label: "Motorwise" },
  { key: "removeMyCar", label: "Remove My Car" },
  { key: "scrapCarComparison", label: "Scrap Car Comparison" },
];

type DashboardStatsExtended = IDashboardStats & {
  vehicleReferrals?: Record<string, Record<string, number | null | undefined> | undefined>;
};

function formatCell(value: number | null | undefined, asPrice: boolean): string {
  if (value == null || Number.isNaN(value)) return "—";
  if (asPrice) return PriceFormat(value);
  return String(value);
}

export default function VehicleReferralsTable({ todayStats, weekStats, monthStats }: IVehicleReferralsTableProps) {
  const [timeframe, setTimeframe] = useState("today");
  const [referrals, setReferrals] = useState<Record<string, Record<string, number | null | undefined> | undefined> | null>(null);

  useEffect(() => {
    const selected =
      timeframe === "week"
        ? weekStats
        : timeframe === "month"
          ? monthStats
          : todayStats;

    setReferrals((selected as DashboardStatsExtended).vehicleReferrals ?? null);
  }, [todayStats, weekStats, monthStats, timeframe]);

  return (
    <DashboardTile span={1} classes="w-full">
      <div className="p-3">
        <div className="mb-3 w-full flex justify-between">
          <div>
            <h3 className="prose font-semibold text-gray-900">Vehicle Referrals</h3>
            <p className="text-sm text-gray-500 mt-0.5">Referral metrics by source</p>
          </div>
          <DashboardStatTableTabs
            tabs={tabs}
            value={timeframe}
            onChange={setTimeframe}
            ariaLabel="Select referral timeframe"
          />
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-center text-base min-w-[800px]">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800 sticky left-0 bg-gray-100 z-10 text-center">
                  Metric
                </th>
                {COLUMN_CONFIG.map((col) => (
                  <th
                    key={col.key}
                    className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800 whitespace-nowrap text-center"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROW_CONFIG.map((row) => (
                <tr key={row.key} className="font-medium border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="py-2.5 pl-4 pr-4 text-gray-700 sticky left-0 bg-white z-10 border-r border-gray-100 text-center">
                    {row.label}
                  </td>
                  {COLUMN_CONFIG.map((col) => {
                    const sourceData = referrals?.[col.key];
                    const value = sourceData?.[row.key];
                    return (
                      <td
                        key={col.key}
                        className="py-2.5 pl-4 pr-4 text-center font-bold text-gray-900 tabular-nums"
                      >
                        {formatCell(value, row.format === "price")}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
